import _invert from "lodash/invert";
import _slugify from "lodash/kebabCase";
import { CheckinType } from "__generated__/ReportsComponentQuery.graphql";

// Comes from webpack build
declare const __STATIC_HOST__: string; // eslint-disable-line no-underscore-dangle
export const STATIC_HOST = __STATIC_HOST__;

export const KNOWLEDGEBASE_URL = "https://employers.workstep.com";
export const ROLE_STATE = {
    OPEN: "open",
    PAUSED: "paused",
    CLOSED: "closed",
};

export const HTML_ENTITY = {
    nbsp: String.fromCharCode(160),
    ndash: String.fromCharCode(8211),
    hellip: String.fromCharCode(8230),

    upchevron: String.fromCharCode(9650),
    rightchevron: String.fromCharCode(9654),
    downchevron: String.fromCharCode(9660),
    leftchevron: String.fromCharCode(9664),

    uparrow: String.fromCharCode(8593),
    downarrow: String.fromCharCode(8595),
};

export const COMPANY_STATE = {
    ARCHIVED: -1,
    ACTIVE: 1,
    UNVERIFIED: 2,
    UNVERIFIED_DUPLICATE: 3,
    UNVERIFIED_FAKE: 4,
};

export const POSITION_STATE = {
    APPROVED: 1,
    PENDING: 2,
    INCOMPLETE: 3,
    EXPANSION: 5,
    ONBOARDING_INCOMPLETE: 6,
    ONBOARDING_STARTED: 7,
    ONBOARDING_COMPLETE: 8,
};
export const POSITION_STATE_LOOKUP = _invert(POSITION_STATE);

export const POSITION_ACTIVE_STATES = [POSITION_STATE.APPROVED, POSITION_STATE.ONBOARDING_COMPLETE];

export const POSITION_HISTORY_REASON = {
    FILLED_WS: 1,
    FILLED_OTHER: 2,
    FILLED_INTERNAL: 3,
    ENOUGH_CANDIDATES: 4,
    NOT_NEEDED: 5,
    CAND_QUALITY: 6,
    CAND_QUANTITY: 7,
    WS_AUTO_PAUSE: 8,
    WS_PAUSE_TO_CLOSE: 9,
    OTHER: 10,
    WS_DASHBOARD: 11,
};

export const NO_CHANGE_MARKER = "Reset";

export const ROLE_TYPE = {
    WAREHOUSE: 1,
    TRUCKING: 2,
    PRODUCTION: 3,
    SKILLED_TRADE: 4,
};
export const ROLE_TYPE_LOOKUP = _invert(ROLE_TYPE);

export const ROLE_TYPE_MAP = {
    [ROLE_TYPE.WAREHOUSE]: 2 ** 3,
    [ROLE_TYPE.TRUCKING]: 2 ** 4,
    [ROLE_TYPE.PRODUCTION]: 2 ** 5,
    [ROLE_TYPE.SKILLED_TRADE]: 2 ** 6,
};

export const ROLE_TYPE_STRING = {
    [ROLE_TYPE_MAP[ROLE_TYPE.WAREHOUSE]]: "Warehouse",
    [ROLE_TYPE_MAP[ROLE_TYPE.TRUCKING]]: "Trucking",
    [ROLE_TYPE_MAP[ROLE_TYPE.PRODUCTION]]: "Production",
    [ROLE_TYPE_MAP[ROLE_TYPE.SKILLED_TRADE]]: "Skilled Trade",
};

export const ROLE_HOURS = {
    FULL_TIME: 0,
    PART_TIME: 1,
};

export const ROLE_HOURS_TYPE = {
    0: "Full-time",
    1: "Part-time",
    2: "Part-time or Full-time",
};

export const START_TIME = {
    DAY: 2 ** 0,
    SWING: 2 ** 1,
    NIGHT: 2 ** 2,
};

export const START_TIMES = {
    [START_TIME.NIGHT]: [
        "4:00AM",
        "4:30AM",
        "5:00AM",
        "5:30AM",
        "6:00AM",
        "6:30AM",
        "7:00AM",
        "7:30AM",
        "8:00AM",
        "8:30AM",
        "9:00AM",
        "9:30AM",
        "10:00AM",
        "10:30AM",
        "11:00AM",
        "11:30AM",
        "12:00PM",
    ],
    [START_TIME.NIGHT]: [
        "12:00PM",
        "12:30PM",
        "1:00PM",
        "1:30PM",
        "2:00PM",
        "2:30PM",
        "3:00PM",
        "3:30PM",
        "4:00PM",
    ],
    [START_TIME.NIGHT]: [
        "4:00PM",
        "4:30PM",
        "5:00PM",
        "5:30PM",
        "6:00PM",
        "6:30PM",
        "7:00PM",
        "7:30PM",
        "8:00PM",
        "8:30PM",
        "9:00PM",
        "9:30PM",
        "10:00PM",
        "10:30PM",
        "11:00PM",
        "11:30PM",
        "12:00AM",
        "12:30AM",
        "1:00AM",
        "1:30AM",
        "2:00AM",
        "2:30AM",
        "3:00AM",
        "3:30AM",
        "4:00AM",
    ],
};

export const SCHEDULE_START_TIMES = [
    "4:00AM",
    "4:30AM",
    "5:00AM",
    "5:30AM",
    "6:00AM",
    "6:30AM",
    "7:00AM",
    "7:30AM",
    "8:00AM",
    "8:30AM",
    "9:00AM",
    "9:30AM",
    "10:00AM",
    "10:30AM",
    "11:00AM",
    "11:30AM",
    "12:00PM",

    "12:30PM",
    "1:00PM",
    "1:30PM",
    "2:00PM",
    "2:30PM",
    "3:00PM",
    "3:30PM",
    "4:00PM",

    "4:30PM",
    "5:00PM",
    "5:30PM",
    "6:00PM",
    "6:30PM",
    "7:00PM",
    "7:30PM",
    "8:00PM",
    "8:30PM",
    "9:00PM",
    "9:30PM",
    "10:00PM",
    "10:30PM",
    "11:00PM",
    "11:30PM",
    "12:00AM",
    "12:30AM",
    "1:00AM",
    "1:30AM",
    "2:00AM",
    "2:30AM",
    "3:00AM",
    "3:30AM",
];

export const SCHEDULE_DAYS = [
    { label: "Monday", value: '"M"' },
    { label: "Tuesday", value: '"TU"' },
    { label: "Wednesday", value: '"W"' },
    { label: "Thursday", value: '"TH"' },
    { label: "Friday", value: '"F"' },
    { label: "Saturday", value: '"SA"' },
    { label: "Sunday", value: '"SU"' },
    { label: "Saturday or Sunday", value: '["SA","SU"]' },
];

export const BASED_ON_ROUTE = "Based on route";

export const REASON_FOR_LEAVING = {
    NEW_JOB_OPPORTUNITY: 1,
    MOVED: 2,
    SCHOOL: 3,
    HEALTH: 4,
    TIME_OFF: 5,
    SCHEDULE: 6,
    TERMINATED: 7,
    OTHER: 8,
    LAID_OFF: 9,
    CONTRACT_ENDED: 10,
    COMMUTE: 11,
};
export const REASON_FOR_LEAVING_LOOKUP = _invert(REASON_FOR_LEAVING);

export const DEGREE_TYPE = {
    HIGH_SCHOOL: 1,
    ASSOCIATES: 2,
    BACHELORS: 3,
    MASTERS: 4,
    OTHER: 5,
    GRADE_SCHOOL: 6,
    TECHNICAL_SCHOOL: 7,
    SOME_COLLEGE: 8,
};
export const DEGREE_TYPE_LOOKUP = _invert(DEGREE_TYPE);

export const MINIMUM_EDUCATION_REQUIREMENT = {
    NONE: 0,
    GED: 1,
    COLLEGE: 2,
};
export const MINIMUM_EDUCATION_REQUIREMENT_LOOKUP = _invert(DEGREE_TYPE);

export const REQUIREMENT_DRUG_TEST = {
    NO: 0,
    YES_CANNABIS_INCLUDED: 1,
    YES_CANNABIS_EXCLUDED: 2,
    YES: 3,
};

export const REJECTION_REASON = {
    OTHER: 0,
    EDUCATION: 2 ** 1,
    AVAILABILITY: 2 ** 2,
    GAPS: 2 ** 3,
    LOCATION: 2 ** 4,
    QUESTION_ANSWERS: 2 ** 5,
    OVERQUALIFIED: 2 ** 6,
    POSITION_CLOSED: 2 ** 7,
    NO_SHOW: 2 ** 8,
    LATE: 2 ** 9,
    ATTITUDE: 2 ** 10,
    ABILITY: 2 ** 11,
    COMMUNICATION: 2 ** 12,
    MISREPRESENTED: 2 ** 13,
    UNSURE: 2 ** 14,
    EXPERIENCE: 2 ** 15,
    REFERRED_TO_ANOTHER_ROLE: 2 ** 16,
    DRUG_SCREEN: 2 ** 17,
    BG_CHECK: 2 ** 18,
    DECLINED_OFFER: 2 ** 19,
    STOPPED_RESPONDING: 2 ** 20,
    LONGEVITY_AT_JOBS: 2 ** 21,
    REASONS_FOR_LEAVING: 2 ** 22,
    EXPIRED: 2 ** 23,
    WITHDRAWN: 2 ** 24,
    DEACTIVATED: 2 ** 25,
    HM_NO_SHOW: 2 ** 26,
    INVITE_RESCINDED: 2 ** 27,
    INVALID_PHONE_NUMBER: 2 ** 28,
    NEVER_STARTED: 2 ** 29,
};

export const REJECTION_REASON_TEXT = {
    [REJECTION_REASON.OTHER]: "Other",
    [REJECTION_REASON.EDUCATION]: "Education",
    [REJECTION_REASON.AVAILABILITY]: "Availability",
    [REJECTION_REASON.GAPS]: "Gaps in work history",
    [REJECTION_REASON.LOCATION]: "Location",
    [REJECTION_REASON.QUESTION_ANSWERS]: "Written question answers",
    [REJECTION_REASON.OVERQUALIFIED]: "Overqualified",
    [REJECTION_REASON.POSITION_CLOSED]: "Position is now closed",
    [REJECTION_REASON.NO_SHOW]: "Didn't show up",
    [REJECTION_REASON.LATE]: "Late",
    [REJECTION_REASON.ATTITUDE]: "Attitude",
    [REJECTION_REASON.ABILITY]: "Knowledge / ability",
    [REJECTION_REASON.COMMUNICATION]: "Communication skills",
    [REJECTION_REASON.MISREPRESENTED]: "Misrepresented experience",
    [REJECTION_REASON.UNSURE]: "Unsure about candidate",
    [REJECTION_REASON.EXPERIENCE]: "Work experience",
    [REJECTION_REASON.REFERRED_TO_ANOTHER_ROLE]: "Referred to another role",
    [REJECTION_REASON.DRUG_SCREEN]: "Failed drug screen",
    [REJECTION_REASON.BG_CHECK]: "Failed background check",
    [REJECTION_REASON.DECLINED_OFFER]: "Declined offer",
    [REJECTION_REASON.STOPPED_RESPONDING]: "Stopped responding",
    [REJECTION_REASON.LONGEVITY_AT_JOBS]: "Longevity at jobs",
    [REJECTION_REASON.REASONS_FOR_LEAVING]: "Reasons for leaving",
    [REJECTION_REASON.EXPIRED]: "Application expired",
    [REJECTION_REASON.WITHDRAWN]: "Candidate withdrew application",
    [REJECTION_REASON.DEACTIVATED]: "Account has been deactivated",
    [REJECTION_REASON.HM_NO_SHOW]: "We missed the interview",
    [REJECTION_REASON.INVITE_RESCINDED]: "Invite rescinded",
    [REJECTION_REASON.INVALID_PHONE_NUMBER]: "Invalid phone number",
    [REJECTION_REASON.NEVER_STARTED]: "Never started",
};

export const MISMATCH_TYPE = {
    QUALIFICATIONS: 1,
    AVAILABILITY: 2,
    LOCATION: 3,
    EXPERIENCE: 4,
    COMMUTE: 5,
    AGE: 6,
};

export const APPLICATION_STATE = {
    APPLIED: 0,
    INTERVIEW: 1,
    REJECTED: 2,
    HIRE_PENDING: 3,
    CANCELED: 4,
    EMPLOYED: 5,
    TERMINATED: 6,
    STARTED: 7,
};
export const APPLICATION_STATE_LOOKUP = _invert(APPLICATION_STATE);

export const PROFILE_STRENGTH = {
    OK: 1,
    GOOD: 2,
    EXCELLENT: 3,
};

export const PROFILE_STRENGTH_TEXT = {
    [PROFILE_STRENGTH.OK]: "OK",
    [PROFILE_STRENGTH.GOOD]: "Good",
    [PROFILE_STRENGTH.EXCELLENT]: "Excellent",
};

export const HIRE_STATE = {
    INVALID: -1,
    EMPLOYED: 1,
    TERMINATED: 2,
};
export const HIRE_STATE_LOOKUP = _invert(HIRE_STATE);

export const HIRE_TYPE = {
    APPLICANT: 1,
    WORKFORCE: 2,
};
export const HIRE_INVALID_REASON = {
    NEVER_HIRED: 1,
    NEVER_STARTED: 2,
    OTHER: 3,
};
export const HIRE_INVALID_REASON_LOOKUP = _invert(HIRE_INVALID_REASON);

export const TERMINATION_REASON = {
    VOLUNTARY_RESIGN: 1,
    VOLUNTARY_ABANDON: 2,
    VOLUNTARY_RETIREMENT: 3,
    VOLUNTARY_OTHER: 4,
    INVOLUNTARY_LAID_OFF: 5,
    INVOLUNTARY_PERFORMANCE: 6,
    INVOLUNTARY_ATTENDANCE: 7,
    INVOLUNTARY_MISCONDUCT: 8,
    INVOLUNTARY_DUTIES: 9,
    INVOLUNTARY_OTHER: 10,
};

export const TERMINATION_REASON_VOLUNTARY = [
    TERMINATION_REASON.VOLUNTARY_RESIGN,
    TERMINATION_REASON.VOLUNTARY_ABANDON,
    TERMINATION_REASON.VOLUNTARY_RETIREMENT,
    TERMINATION_REASON.VOLUNTARY_OTHER,
];

export const TERMINATION_REASON_INVOLUNTARY = [
    TERMINATION_REASON.INVOLUNTARY_LAID_OFF,
    TERMINATION_REASON.INVOLUNTARY_PERFORMANCE,
    TERMINATION_REASON.INVOLUNTARY_ATTENDANCE,
    TERMINATION_REASON.INVOLUNTARY_MISCONDUCT,
    TERMINATION_REASON.INVOLUNTARY_DUTIES,
    TERMINATION_REASON.INVOLUNTARY_OTHER,
];

export const RETENTION_CALCULATION_TYPE = {
    DEFAULT: 1,
    SHRM: 2,
};

export const REQUIRED_FOR_INTERVIEW = {
    RESUME: 2 ** 0,
    ID: 2 ** 1,
    MEDICAL: 2 ** 2,
    MVR: 2 ** 3,
    SS: 2 ** 4,
};

export const REQUIRED_FOR_INTERVIEW_STRING = {
    [REQUIRED_FOR_INTERVIEW.RESUME]: "Résumé",
    [REQUIRED_FOR_INTERVIEW.ID]: "Government-issued ID",
    [REQUIRED_FOR_INTERVIEW.MEDICAL]: "DOT medical card",
    [REQUIRED_FOR_INTERVIEW.MVR]: "MVR report",
    [REQUIRED_FOR_INTERVIEW.SS]: "Social Security card",
};

export const REQUIRED_WEAR_FOR_INTERVIEW = {
    SHOES: 2 ** 0,
};

export const REQUIRED_WEAR_FOR_INTERVIEW_STRING = {
    [REQUIRED_WEAR_FOR_INTERVIEW.SHOES]: "Closed-toe shoes",
};

// This is clearly still used for its original intention!
export const REVIEW_MODE = {
    EMPTY: -1,
    START: 0,
    INTERVIEW_FOLLOWUP: 1,
    PENDING_FOLLOWUP: 2,
    CANDIDATE_REVIEW: 3,
    FOLLOWUP_START: 4,
    MORE_CANDIDATES_PROMPT: 5,
    CAPACITY_REVIEW: 6,

    ROLES: 996,
    CANDIDATE_RESUME: 997,
    FINISH: 999,
};

export const PIPELINE_PANE = {
    UNREVIEWED: 1,
    INVITED: 2,
    INTERVIEWED: 3,
    PENDING: 4,
    HIRED: 5,
    SEARCH: 6,
    EEOC_OFCCP_EXPORT: 7,
};
export const PIPELINE_PANE_LOOKUP = _invert(PIPELINE_PANE);

export const PIPELINE_APPLICATION_TYPE = {
    UNREVIEWED: 1,
    INVITED: 2,
    PENDING: 3,
    HIRED: 4,
};
export const PIPELINE_APPLICATION_TYPE_LOOKUP = _invert(PIPELINE_APPLICATION_TYPE);

export const PIPELINE_NAV_TEXT = {
    [PIPELINE_PANE.UNREVIEWED]: "New candidates",
    [PIPELINE_PANE.INVITED]: "Invited to interview",
    [PIPELINE_PANE.INTERVIEWED]: "Needs feedback",
    [PIPELINE_PANE.PENDING]: "Moving forward",
    [PIPELINE_PANE.HIRED]: "Hired",
    [PIPELINE_PANE.SEARCH]: "Search",
    [PIPELINE_PANE.EEOC_OFCCP_EXPORT]: "EEOC/OFCCP Export",
};

export const PIPELINE_PANE_SLUG = Object.entries(PIPELINE_NAV_TEXT).reduce(
    (memo, [k, v]) => ({
        ...memo,
        [k]: _slugify(v),
    }),
    {},
);
export const PIPELINE_PANE_SLUG_LOOKUP = _invert(PIPELINE_PANE_SLUG);

export const SHIFT_PREFERENCE = {
    DEFINITELY_NOT: 1,
    PREFERABLY_NOT: 2,
    OKAY: 3,
    PREFERRED: 4,
};

export const VETERAN_STATUS = {
    IS_VETERAN: 1,
    NOT_VETERAN: 2,
    DECLINED: 3,
};

export const DISABILITY_STATUS = {
    IS_DISABLED: 1,
    NOT_DISABLED: 2,
    DECLINED: 3,
};

export const QUESTION_TYPE = {
    CHOOSE_ONE: 1,
    SHORT_ANSWER: 2,
    LONG_ANSWER: 3,
    CHOOSE_MANY: 4,
    FOLLOW_UP: 5,
    SELECT_ONE: 6,
    RATE_ONE: 7,
    RATE_SATISFACTION: 8,
    ENPS: 9,
    RATE_ONE_TEN_SCALE: 10,
};
export const QUESTION_TYPE_LOOKUP = _invert(QUESTION_TYPE);

// question types we use the "multiple selection" component for in retain
export const QUESTION_TYPE_RETAIN_MULTI = [
    QUESTION_TYPE.CHOOSE_ONE,
    QUESTION_TYPE.SELECT_ONE,
    QUESTION_TYPE.CHOOSE_MANY,
    QUESTION_TYPE.FOLLOW_UP,
];

export const QUESTION_TEXT_TYPES = [
    QUESTION_TYPE.SHORT_ANSWER,
    QUESTION_TYPE.LONG_ANSWER,
    QUESTION_TYPE.FOLLOW_UP,
];

export const QUESTION_RATE_TYPES = [
    QUESTION_TYPE.RATE_SATISFACTION,
    QUESTION_TYPE.RATE_ONE,
    QUESTION_TYPE.RATE_ONE_TEN_SCALE,
];

export const QUESTION_RATE_DEFAULT_TAG = "Choose One";

export const QUESTION_SINGLE_TYPES = [QUESTION_TYPE.CHOOSE_ONE, QUESTION_TYPE.SELECT_ONE];

export const QUESTION_TYPE_SHORT_CHART_TEXT_SEARCH = [
    { value: "agree", text: "Agree" },
    { value: "likely", text: "Likely" },
    { value: "good", text: "Good" },
    { value: "easy", text: "Easy" },
    { value: "yes", text: "Yes" },
];

export const QUESTION_TYPE_TEXT_MAP = {
    open: "Open Text",
    multiple: "Multiple Choice",
    single: "Single Select",
};

export const QUESTION_TYPE_TEXT_SEARCH = [
    { value: "agree", text: "Agreeable Scale" },
    { value: "likely", text: "Likely Scale" },
    { value: "happy", text: "Happiness Scale" },
    { value: "yes", text: "Yes/No" },
];

export const QUESTION_SOURCE = {
    COMPANY_QUESTION: 1,
    CHECK_IN_7D: 4,
    CHECK_IN_TERM: 5,
    CHECK_IN_30D: 6,
    CHECK_IN_90D: 7,
    CHECK_IN_CUSTOM: 8,
};
export const QUESTION_SOURCE_LOOKUP = _invert(QUESTION_SOURCE);

export const QUESTION_TRANSLATION_STATE = {
    NOT_STARTED: 0,
    IN_PROGRESS: 1,
    IN_REVIEW: 3,
    APPROVED: 2,
};
export const QUESTION_TRANSLATION_STATE_LOOKUP = _invert(QUESTION_TRANSLATION_STATE);

export const LONG_ANSWER_CHARS = 60;

export const ANSWER_SOURCE = {
    APP: 1,
    PROFILE: 2,
    ONBOARD: 3,
    CHECK_IN_7D: 4,
    CHECK_IN_TERM: 5,
    CHECK_IN_30D: 6,
    CHECK_IN_90D: 7,
};

export const SUGGESTION_SOURCE = {
    APP: 1,
    ONBOARD: 2,
    MATCHES: 3,
};

export const CANDIDATE_SEARCH_STATUS = {
    UNEMPLOYED_ACTIVE: 1,
    EMPLOYED_ACTIVE: 2,
    EMPLOYED_PASSIVE: 3,
    EMPLOYED_INACTIVE: 4,
};

export const CANDIDATE_FEEDBACK_STATE = {
    REQUESTED_NOT_RECEIVED: 0,
    GOOD_NEXT_STEPS: 1,
    NOT_SURE: 2,
    COMPANY_PASSED: 3,
    NO_LONGER_INTERESTED: 4,
    NO_SHOW: 5,
    HM_NO_SHOW: 6,
};

export const INTERVIEW_TYPE = {
    IN_PERSON: 1,
    PHONE_SCREEN: 2,
    INFO_SESSION: 3,
    VIDEO_CALL: 4,
    CUSTOM: 5,
};

export const INTERVIEW_TYPE_STRING = {
    [INTERVIEW_TYPE.IN_PERSON]: "In Person",
    [INTERVIEW_TYPE.PHONE_SCREEN]: "Phone Screen",
    [INTERVIEW_TYPE.INFO_SESSION]: "Info Session",
    [INTERVIEW_TYPE.VIDEO_CALL]: "Video Call",
    [INTERVIEW_TYPE.CUSTOM]: "Custom",
};

export const NO_RESCHEDULE_REASON = {
    NO_RESCHEDULE: "ws_no_reschedule",
    CAND_REPORTED_HM_NO_SHOW: "ws_cand_reported_hm_noshow",
};

export const COMMUTE_TYPE = {
    DRIVING: 2 ** 0,
    TRANSIT: 2 ** 1,
};

export const QUESTION_FLOW = {
    ONBOARD: 1,
    PROFILE: 2,
    CHECK_IN: 3,
    APP: 4,
};

export const SUGGESTION_FEATURE = {
    HIGH_CAPACITY: 1,
    SHORT_COMMUTE: 2,
    AUTOMATCH: 3,
};

export const ACCOUNT_TYPE = {
    APPLICANT: 1,
    HIRING_MANAGER: 2,
    TRAINING: 3,
    WORKFORCE: 4,
    DEVELOPER: 5,
};

export const CHECKIN_TYPE: Record<string, number> = {
    MILESTONE: 1,
    TERMINATION: 2,
    CUSTOM: 3,
    RECURRING: 4,
    ALWAYS_ON: 5,
    KIOSK: 6,
};

export const CHECKIN_SUBTYPE = {
    ANNUAL_SURVEY: 1,
    CONTINUOUS: 2,
};

export const COMPANY_MESSAGE_TYPE = { INFORM: "INFORM", SURVEY: "SURVEY" };

export const CHECKIN_SENDABLE = [
    CHECKIN_TYPE.MILESTONE,
    CHECKIN_TYPE.RECURRING,
    CHECKIN_TYPE.TERMINATION,
    CHECKIN_TYPE.CUSTOM,
];

export const CHECKIN_SURVEY_STATUS = {
    CREATED: "Created",
    SCHEDULED: "Scheduled",
    ACTIVE: "Active",
    ENDED: "Ended",
    INVALID: "Invalid",
} as const;

export type CheckinSurveyStatus = typeof CHECKIN_SURVEY_STATUS[keyof typeof CHECKIN_SURVEY_STATUS];

export const SURVEY_STATE: Record<string, string> = {
    DRAFT: "DRAFT",
    ENABLED: "ENABLED",
    DISABLED: "DISABLED",
    DELETED: "DELETED",
};
export const SURVEY_STATE_INT: Record<string, number> = {
    [SURVEY_STATE.DRAFT]: 0,
    [SURVEY_STATE.ENABLED]: 1,
    [SURVEY_STATE.DISABLED]: 2,
    [SURVEY_STATE.DELETED]: 3,
};
export const SURVEY_STATE_LOOKUP = _invert(SURVEY_STATE_INT);

export const ANNUAL_SURVEY_DEFAULT_TITLE = "Employee Engagement Survey";

export const USER_ACTION_STATE = {
    DEFAULT: 0,
    PULSE_INTRO_MODAL_ANNOUNCEMENT: 2 ** 0,
    HM_FIRST_INVITE_NOTIFICATION: 2 ** 1,
    HM_MOVING_FORWARD_NOTIFICATION: 2 ** 2,
    HM_WORKFORCE_CSV_UPLOAD: 2 ** 3, // Deprecated
    HM_EXPANSION_ROLE_BANNER: 2 ** 5,
    HM_ALERT_CAPACITY_BANNER: 2 ** 6,
    TOS_PRIVACY_UPDATE: 2 ** 7,
    RETAIN_INTRO: 2 ** 8,
    RETAIN_ALERTS_TOOLTIP: 2 ** 9, // Deprecated
    HIRE_COUNTS_MODAL: 2 ** 10,
};
export const USER_ACTION_STATE_STR = Object.keys(USER_ACTION_STATE).reduce(
    (objStr, val) => ({
        ...objStr,
        [val]: val,
    }),
    {},
);

export const EVENT_ENTITY = {
    COMPANY: 1,
    ORGANIZATION: 2,
};

export const AUTH_TYPE = {
    PASSWORD: 1,
    VERIFY: 2,
    LINK: 3,
    RESET: 4,
    LOCKED: 5,
};

export const AUTH_CODE_TYPE = {
    MOBILE: "mobile",
    EMAIL: "email",
};

// see also: LoginLevel on the backend
export const LOGIN_LEVEL = {
    NORMAL: 1,
    EXPIRED: 2,
    DEACTIVATED: 3,
    RESET: 4,
    SUSPENDED: 5,
    PW_CHANGED: 6,
    TIMEOUT: 7,
};

export const AUTH_ERROR = {
    UNKNOWN: -1,
    FIRST_NAME_REQUIRED: 1,
    LAST_NAME_REQUIRED: 2,
    INVALID_PHONE: 3,
    PHONE_TAKEN: 4,
    INVALID_EMAIL: 5,
    EMAIL_TAKEN: 6,
    PASSWORD_LENGTH: 7,
    PASSWORD_STRENGTH: 8,
    INVALID_HANDLE: 9,
    DUPLICATE_USER: 10,
};

export const MOBILE_BREAK = 768;

export const ADP_PLAN = {
    NONE: 1,
    UNCONSENTED: 2,
    SUBSCRIBED: 3,
    UNSUBSCRIBED: 4,
};

export const RETAIN_FAVORABLE_THRESHOLD = 4;
export const RETAIN_COUNT_THRESHOLD = 20;

export const RETAIN_DEFAULT_DATE_RANGE_DAYS = [90, 180];
export const RETAIN_EXTENDED_DATE_RANGE_DAYS = [7, 30, 60, 90, 180];
export const RETAIN_DEFAULT_DATE_RANGE_OPTION = 180;

export const DATE_RANGE_OVER_TIME_DAYS = [90, 180, 365];
export const DATE_RANGE_OVER_TIME_DEFAULT = 365;

export const BENCHMARK_CATEGORY = {
    Custodial: 1,
    Driver: 2,
    "General Labor": 3,
    Managerial: 4,
    Production: 5,
    Retail: 6,
    Sales: 7,
    Technician: 8,
    Warehouse: 9,
};
export const BENCHMARK_CATEGORY_LOOKUP = _invert(BENCHMARK_CATEGORY);

export const REMINDER_TYPE = {
    SAVE_APPLICATION: 1,
    UPLOAD_RESUME: 2,
};

export const RESUME_STORAGE_KEY = "attemptedResumeParse";

export const TOAST_TYPE = {
    INFO: 1,
    WARNING: 2,
    ERROR: 3,
};

export const PERMISSION = {
    HIRE: "hire",
    RETAIN: "retain",
    CALENDAR: "hire",
    PERMISSION_ROLE: "permission_role",
    PIPELINE: "hire",
    PULSE: "retain",
    ROLE_DETAILS: "hire",
};

export const PERMISSION_ROLE = {
    NONE: 0,
    VIEWER: 1,
    EDITOR: 2,
    CUSTOM: 3,
    ADMIN: 4,
};

export const PERMISSION_ROLE_LOOKUP = _invert(PERMISSION_ROLE);

export const PERMISSION_TYPE = {
    NONE: 0,
    VIEW: 1,
    EDIT: 2,
    ADMIN: 99,
};

export const PERMISSION_TYPE_LOOKUP = _invert(PERMISSION_ROLE);

export const PERMISSION_ROLE_TYPE = {
    [PERMISSION_ROLE.NONE]: PERMISSION_TYPE.NONE,
    [PERMISSION_ROLE.VIEWER]: PERMISSION_TYPE.VIEW,
    [PERMISSION_ROLE.EDITOR]: PERMISSION_TYPE.EDIT,
    [PERMISSION_ROLE.ADMIN]: PERMISSION_TYPE.ADMIN,
};

export const PHONE_STATE = {
    UNVERIFIED: 0,
    VERIFIED: 1,
    CHANGED: 2,
};

export const PHONE_TYPE = {
    INVALID: 0,
    MOBILE: 1,
    LANDLINE: 2,
    VOIP: 3,
};

export const EMAIL_BOUNCE_TYPE = {
    PERMANENT: 1,
    TRANSIENT: 2,
    UNDETERMINED: 3,
};

export const KEY_CODE = {
    CANCEL: 3,
    HELP: 6,
    BACK_SPACE: 8,
    TAB: 9,
    CLEAR: 12,
    RETURN: 13,
    ENTER: 14,
    SHIFT: 16,
    CONTROL: 17,
    ALT: 18,
    PAUSE: 19,
    CAPS_LOCK: 20,
    ESCAPE: 27,
    SPACE: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    PRINTSCREEN: 44,
    INSERT: 45,
    DELETE: 46,
    NUM_0: 48,
    NUM_1: 49,
    NUM_2: 50,
    NUM_3: 51,
    NUM_4: 52,
    NUM_5: 53,
    NUM_6: 54,
    NUM_7: 55,
    NUM_8: 56,
    NUM_9: 57,
    SEMICOLON: 59,
    EQUALS: 61,
    A: 65,
    B: 66,
    C: 67,
    D: 68,
    E: 69,
    F: 70,
    G: 71,
    H: 72,
    I: 73,
    J: 74,
    K: 75,
    L: 76,
    M: 77,
    N: 78,
    O: 79,
    P: 80,
    Q: 81,
    R: 82,
    S: 83,
    T: 84,
    U: 85,
    V: 86,
    W: 87,
    X: 88,
    Y: 89,
    Z: 90,
    CONTEXT_MENU: 93,
    NUMPAD0: 96,
    NUMPAD1: 97,
    NUMPAD2: 98,
    NUMPAD3: 99,
    NUMPAD4: 100,
    NUMPAD5: 101,
    NUMPAD6: 102,
    NUMPAD7: 103,
    NUMPAD8: 104,
    NUMPAD9: 105,
    MULTIPLY: 106,
    ADD: 107,
    SEPARATOR: 108,
    SUBTRACT: 109,
    DECIMAL: 110,
    DIVIDE: 111,
    F1: 112,
    F2: 113,
    F3: 114,
    F4: 115,
    F5: 116,
    F6: 117,
    F7: 118,
    F8: 119,
    F9: 120,
    F10: 121,
    F11: 122,
    F12: 123,
    F13: 124,
    F14: 125,
    F15: 126,
    F16: 127,
    F17: 128,
    F18: 129,
    F19: 130,
    F20: 131,
    F21: 132,
    F22: 133,
    F23: 134,
    F24: 135,
    NUM_LOCK: 144,
    SCROLL_LOCK: 145,
    COMMA: 188,
    PERIOD: 190,
    SLASH: 191,
    BACK_QUOTE: 192,
    OPEN_BRACKET: 219,
    BACK_SLASH: 220,
    CLOSE_BRACKET: 221,
    QUOTE: 222,
    META: 224,
};

export const US_STATES = [
    ["AK", "Alaska"],
    ["AL", "Alabama"],
    ["AR", "Arkansas"],
    ["AZ", "Arizona"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["CT", "Connecticut"],
    ["DE", "Delaware"],
    ["FL", "Florida"],
    ["GA", "Georgia"],
    ["HI", "Hawaii"],
    ["IA", "Iowa"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["KS", "Kansas"],
    ["KY", "Kentucky"],
    ["LA", "Louisiana"],
    ["MA", "Massachusetts"],
    ["MD", "Maryland"],
    ["ME", "Maine"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MO", "Missouri"],
    ["MS", "Mississippi"],
    ["MT", "Montana"],
    ["NC", "North Carolina"],
    ["ND", "North Dakota"],
    ["NE", "Nebraska"],
    ["NH", "New Hampshire"],
    ["NJ", "New Jersey"],
    ["NM", "New Mexico"],
    ["NV", "Nevada"],
    ["NY", "New York"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["PA", "Pennsylvania"],
    ["RI", "Rhode Island"],
    ["SC", "South Carolina"],
    ["SD", "South Dakota"],
    ["TN", "Tennessee"],
    ["TX", "Texas"],
    ["UT", "Utah"],
    ["VA", "Virginia"],
    ["VT", "Vermont"],
    ["WA", "Washington"],
    ["WI", "Wisconsin"],
    ["WV", "West Virginia"],
    ["WY", "Wyoming"],
    ["DC", "District of Columbia"],
];

// Turns out getting moment to list these is a WHOLE THING.
export const US_TIMEZONES = [
    "America/Adak",
    "America/Anchorage",
    "America/Boise",
    "America/Chicago",
    "America/Denver",
    "America/Detroit",
    "America/Indiana/Indianapolis",
    "America/Indiana/Knox",
    "America/Indiana/Marengo",
    "America/Indiana/Petersburg",
    "America/Indiana/Tell_City",
    "America/Indiana/Vevay",
    "America/Indiana/Vincennes",
    "America/Indiana/Winamac",
    "America/Juneau",
    "America/Kentucky/Louisville",
    "America/Kentucky/Monticello",
    "America/Los_Angeles",
    "America/Menominee",
    "America/Metlakatla",
    "America/New_York",
    "America/Nome",
    "America/North_Dakota/Beulah",
    "America/North_Dakota/Center",
    "America/North_Dakota/New_Salem",
    "America/Phoenix",
    "America/Sitka",
    "America/Yakutat",
    "Pacific/Honolulu",
];

export const US_TIMEZONE_LOOKUP = {
    "US Eastern (ET)": "America/New_York",
    "US Central (CT)": "America/Chicago",
    "US Mountain (MT)": "America/Denver",
    "US Pacific (PT)": "America/Los_Angeles",
};

export const DASHBOARD_SEARCH_TYPE = {
    COMPANY: 1,
    FACILITY: 2,
    USER: 3,
    POSITION: 4,
    FACILITY_ATTRIBUTE: 5,
};

export const DASHBOARD_SEARCH_TAB = {
    ROLES: "Roles",
    MANAGERS: "Managers",
    QUESTIONS: "Questions",
    CHECKINS: "Surveys",
    SETTINGS: "Settings",
};

export const DASHBOARD_SEARCH_TAB_PATH = {
    [DASHBOARD_SEARCH_TAB.ROLES]: "roles",
    [DASHBOARD_SEARCH_TAB.MANAGERS]: "managers",
    [DASHBOARD_SEARCH_TAB.QUESTIONS]: "questions",
    [DASHBOARD_SEARCH_TAB.CHECKINS]: "checkins",
    [DASHBOARD_SEARCH_TAB.SETTINGS]: "settings",
};

export const DASHBOARD_SEARCH_TAB_PATH_LOOKUP = _invert(DASHBOARD_SEARCH_TAB_PATH);

export const SORT_DIRECTION = {
    ASC: "asc",
    DESC: "desc",
};

export const ALERT_STATE = {
    INVALID: -1,
    OPEN: 1,
    RESOLVED: 2,
};

export const ALERT_TYPE = {
    ALERT: 1,
    COMMENT: 2,
    POSITIVE: 3,
};

export const ALERT_TYPE_NAME = {
    [ALERT_TYPE.ALERT]: "Alert",
    [ALERT_TYPE.COMMENT]: "Comment",
    [ALERT_TYPE.POSITIVE]: "Positive",
};

export const ANSWER_ITEM_TYPE = {
    REPLY: 1,
    NOTE: 2,
};

export const ANSWER_GROUP_REPLY_TYPE = {
    HIRING_MANAGER: 1,
    NOTE: 2,
    EMPLOYEE: 3,
};

export const RETAIN_FILTER_TYPE = {
    LOC: "l",
    ORG: "o",
};

export const RETAIN_FILTER_TYPE_LOOKUP = _invert(RETAIN_FILTER_TYPE);

export const ROSTER_FILE_STATE = {
    NEW: 1,
    PENDING: 2,
    SUCCESS: 3,
    ERROR: 4,
};

export const PERIOD_TYPE = {
    WEEKLY: "weekly",
    MONTHLY: "monthly",
    QUARTERLY: "quarterly",
    SINGLE: "single",
} as const;

export type PeriodType = typeof PERIOD_TYPE[keyof typeof PERIOD_TYPE];

export const PERIOD_TYPE_KEYS = _invert(PERIOD_TYPE);

// Height in pixels of the top fixed nav in desktop mode - used for scroll offset
export const TOP_NAV_HEIGHT = 88;

export const LANGUAGE_OPTIONS = [
    { value: "en", label: "English" },
    { value: "da", label: "Dansk" },
    { value: "es", label: "Español" },
    { value: "fr", label: "Français" },
    { value: "ht_HT", label: "Kreyòl Ayisyen" },
    { value: "pl", label: "Polski" },
    { value: "pt", label: "Português" },
    { value: "ro", label: "Română" },
    { value: "tl", label: "Tagalog" },
    { value: "vi", label: "Tiếng Việt" },
    { value: "ko", label: "한국어" },
    { value: "zh", label: "中国人" },
    { value: "yue", label: "粵語" },
    { value: "ne", label: "नेपाली" },
    { value: "pa_IN", label: "ਪੰਜਾਬੀ" },
    { value: "ksw", label: "ကညီကျိာ်" },
];

export const LANGUAGE_EN_LABEL_BY_CODE = {
    en: "English",
    es: "Spanish",
    fr: "French",
    ksw: "S'gaw Karen",
    ne: "Nepali",
    pa_IN: "Punjabi",
    pl: "Polish",
    pt: "Portuguese",
    vi: "Vietnamese",
    ko: "Korean",
    zh: "Mandarin - Simplified",
    yue: "Cantonese - Logographic",
    tl: "Tagalog",
    fil: "Filipino",
    ht_HT: "Haitian Creole",
    da: "Danish",
    ro: "Romanian",
};

export const LANGUAGE_SOURCE = {
    BROWSER: 1,
    USER: 2,
    ROSTER: 3,
};

export const PORTAL_NAV_BREAKPOINTS = {
    sm: 0,
    md: 999,
    lg: 1210,
};

// These are the MUI defaults
export const STANDARD_BREAKPOINTS = {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
};

export const REQUEST_QUEUE_KEY = {
    KIOSK_ANSWERS: "kiosk-answers",
    RETAIN_COMMENTS: "retain-comments",
    ANSWER_CHUNK: "answer-chunk",
};

export const LOW_DATA_MESSAGE = {
    RETAIN: "Low sample size for this segment.",
};

export const REPLY_TEMPLATE_KINDS = {
    SYSTEM: "system", // built-in
    SHARED: "shared", // shared in a company
    USER: "user", // specfic to a user
};

export const WORKFORCE_ALWAYS_ON_PHONE = "+18885214493";
export const WORKFORCE_ALWAYS_ON_SHORTCODE = "31794";

export const FACILITY_CHECKED_STATES = {
    TRUE: "true",
    FALSE: "false",
    UNCHECKALL: "uncheckAll",
};

export const INDETERMINATE_VALUE = {};

export const EXCLUDED_FOLLOWUP_QUESTIONS = [
    "Please describe:",
    "Can you provide more detail?",
    "Can you provide more detail",
    "Please explain",
    "Can you provide any examples?",
];

export const MULTI_SELECT_FOLLOW_UP_DELIMETER = "|";

export const DEFAULT_MULTI_SELECT_MIN_SELECTIONS = 1;
export const DEFAULT_MULTI_SELECT_MAX_SELECTIONS = null;

export const KIOSK_SHIFT_OPTIONS = [
    { text: "Early Morning", meta: "1a - 5a", value: "1,2,3,4,5" },
    { text: "Morning", meta: "5a - 9a", value: "5,6,7,8,9" },
    { text: "Midday", meta: "9a - 1p", value: "10,11,12,13" },
    { text: "Afternoon", meta: "1p - 5p", value: "13,14,15,16,17" },
    { text: "Evening", meta: "5p - 9p", value: "17,18,19,20,21" },
    { text: "Night", meta: "9p - 1a", value: "21,22,23,0,1" },
];

export const FAVORABLE_ANSWER_ID = ["a4", "a5"];

export const COMPANY_FEATURE = {
    LISTEN: "LISTEN",
    ACT: "ACT",
    INFORM: "INFORM",
};

export const INFORM_MESSAGE_STATE = {
    DRAFT: "draft",
    SCHEDULED: "scheduled",
    SENT: "sent",
};

export const COMPANY_SEGMENT_STATUS = {
    ACTIVE: 1,
    DELETED: 2,
};

export const ALERT_ASSIGNMENT_COLUMNS = {
    NAME: "name",
    UNRESOLVED: "unresolved",
    OVER: "over",
    PERC_RESOLVED: "perc_resolved",
    AVG_TIME: "avg_time",
};

export const CHECKIN_MANAGEMENT_TAB_INDEX = {
    MILESTONE: 0,
    CUSTOM: 1,
};

// Baseline minimum WorkStep survey group size to protect
// identifiable data  has a floor of 3
// Use the getCompanySurveyGroupThreshold selector to get company-specific floor
export const WORKSTEP_SURVEY_GROUP_MINIMUM = 3;

export const SENTIMENT_SOURCE = { POSITIVE: 1, NEGATIVE: 2, NEUTRAL: 3, UNKNOWN: 4 };

export const LOCATION_BREAKDOWN_FAVORABILITY_TOOLTIP =
    "The General Satisfaction employee engagement theme factors into the overall " +
    "favorability score by location.";

export const REPORTS_ATTRIBUTE_MAX_ROWS = 60;

export const REPORTS_IMPACT_TRACKER_MAX_ROWS = 7;

export const DEFAULT_CHECKIN_TYPES: CheckinType[] = ["MILESTONE", "RECURRING"];
